import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";

// --- Spark Logic --- 

// Base64 encoded simple spark image (yellowish star/burst)
const sparkImageDataUrl = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MCA1MCI+PHBvbHlnb24gcG9pbnRzPSIyNSwzLjggMzAuOSwxOS4xIDQ3LjIsMTkuMSAzNC4yLDI4LjggMzkuMSw0NC4yIDI1LDM0LjUgMTAuOSw0NC4yIDE1LjgsMjguOCAyLjgsMTkuMSAxOS4xLDE5LjEgMjUsMy44IiBmaWxsPSIjRkZFNTAwIiBvcGFjaXR5PSIwLjkiLz48cG9seWdvbiBwb2ludHM9IjI1LDguMyAyOS42LDIwLjMgNDIuMywyMC4zIDMyLjgsMjguMyAzNy40LDQwLjcgMjUsMzMuNiAxMi42LDQwLjcgMTcuMiwyOC4zIDcuNywyMC4zIDIwLjQsMjAuMyAyNSw4LjMiIGZpbGw9IiNGRkQ3MDAiIG9wYWNpdHk9IjAuNyIvPjwvc3ZnPg==";

// Define CSS keyframes for the spark animation
const sparkAnimation = `
@keyframes spark-burst-component {
  0% {
    transform: scale(0.6) translate(0, 0);
    opacity: 1;
  }
  70% { 
    opacity: 0.8; 
  }
  100% {
    transform: scale(0.9) translate(var(--spark-x), var(--spark-y));
    opacity: 0;
  }
}
`;

// Inject CSS Keyframes - runs only once per module evaluation
if (typeof window !== 'undefined') {
    if (!document.getElementById('spark-burst-component-animation')) {
        const styleEl = document.createElement('style');
        styleEl.id = 'spark-burst-component-animation';
        styleEl.innerHTML = sparkAnimation;
        document.head.appendChild(styleEl);
    }
}

interface Spark {
    id: number;
    x: number;
    y: number;
    duration: number;
    size: number;
    createdAt: number;
    maxLifetime: number;
}

// Define default spark settings OUTSIDE the component
const defaultSparkSettingsConst = {
  emissionRate: 12,
  interval: 100,
  colors: [
    "#FFD700",
    "#FFA500",
    "#FFEA00"
  ],
  minDistance: 18,
  maxDistance: 200,
  minDuration: 0.7,
  maxDuration: 0.8,
  minSize: 8,
  maxSize: 14
};

interface SparkSettings {
    emissionRate?: number;
    interval?: number;
    colors?: string[];
    minDistance?: number;
    maxDistance?: number;
    minDuration?: number;
    maxDuration?: number;
    minSize?: number;
    maxSize?: number;
}

type SparksRendererProps = Required<SparkSettings> & { isAnimating: boolean };

const SparksRenderer: React.FC<SparksRendererProps> = React.memo(({
    emissionRate,
    interval,
    colors,
    minDistance,
    maxDistance,
    minDuration,
    maxDuration,
    minSize,
    maxSize,
    isAnimating // Control emission based on parent animation state
}) => {
    const [sparks, setSparks] = useState<Spark[]>([]);
    const nextId = useRef(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isAnimating) {
            intervalRef.current = setInterval(() => {
                const now = performance.now();
                const newSparks: Spark[] = []; // Explicit type annotation
                const distanceRange = maxDistance - minDistance;
                const durationRange = maxDuration - minDuration;
                const sizeRange = maxSize - minSize;

                for (let i = 0; i < emissionRate; i++) {
                    const angle = Math.random() * 2 * Math.PI;
                    const distance = minDistance + Math.random() * distanceRange;
                    const duration = minDuration + Math.random() * durationRange;
                    const id = nextId.current++;

                    newSparks.push({
                        id: id,
                        x: Math.cos(angle) * distance,
                        y: Math.sin(angle) * distance,
                        duration: duration,
                        size: minSize + Math.random() * sizeRange,
                        createdAt: now,
                        maxLifetime: duration * 1000
                    });
                }

                setSparks(prevSparks => {
                    const filteredSparks = prevSparks.filter(spark => now < spark.createdAt + spark.maxLifetime);
                    const newState = [...filteredSparks, ...newSparks];
                    return newState;
                });
            }, interval);

        } else {
             if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
             }
        }

        // Cleanup function
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };

    }, [isAnimating, emissionRate, interval, colors, minDistance, maxDistance, minDuration, maxDuration, minSize, maxSize]);

    // Cleanup sparks completely when component unmounts or isAnimating becomes false and interval is cleared
    useEffect(() => {
        if (!isAnimating && !intervalRef.current) {
            const cleanupTimeout = setTimeout(() => {
                setSparks([]);
            }, (maxDuration * 1000) + 100); // maxDuration in ms + buffer
            return () => clearTimeout(cleanupTimeout);
        }
    }, [isAnimating, maxDuration]);


    const sparkElements = useMemo(() => sparks.map((spark) => (
        <img
            key={spark.id}
            src={sparkImageDataUrl}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                ['--spark-x' as any]: `${spark.x}px`,
                ['--spark-y' as any]: `${spark.y}px`,
                width: `${spark.size}px`,
                height: `${spark.size}px`,
                transform: `translate(-50%, -50%)`,
                animation: `spark-burst-component ${spark.duration}s ease-out forwards`,
                opacity: 0,
                willChange: 'transform, opacity',
                pointerEvents: 'none',
            }}
        />
    )), [sparks]);

    return (
        <div style={{ position: 'absolute', top: '50%', left: '50%', width: 0, height: 0, zIndex: 2 }}>
            {sparkElements}
        </div>
    );
});

// --- End Spark Logic ---

// --- Main Component --- 

interface Props {
    targetBalance: string;
    onAnimationComplete: () => void;
    duration?: number;
    sparkSettings?: SparkSettings;
    fontSize?: number; // Add font size prop
}

const AnimatedBalanceDisplay: React.FC<Props> = ({
    targetBalance,
    onAnimationComplete,
    duration = 3000,
    sparkSettings = {},
    fontSize = 14 // Default font size (px)
}) => {

    // --- Width Calculation Logic ---
    const hiddenSpanRef = useRef<HTMLSpanElement>(null);
    const [textWidth, setTextWidth] = useState<number | null>(null);

    useEffect(() => {
        // Measure the width of the final targetBalance string when it changes
        if (hiddenSpanRef.current) {
            const width = hiddenSpanRef.current.offsetWidth;
            setTextWidth(width);
        }
    }, [targetBalance, fontSize]); // Re-calculate if target or font size changes
    // --- End Width Calculation Logic ---

    // --- Rolling Number Logic ---
    const [displayValue, setDisplayValue] = useState("$0.00");
    const [isRolling, setIsRolling] = useState(true);
    const frameRef = useRef<number>();
    const startTimeRef = useRef<number>();

    // 1. Extract leading non-numeric characters as the symbol
    const currencySymbolMatch = targetBalance.match(/^[^0-9.]+/);
    const currencySymbol = currencySymbolMatch ? currencySymbolMatch[0] : ''; // Default to empty string if no symbol found

    // 2. Parse the numeric value by removing non-digits/periods
    const targetNumericValue = parseFloat(targetBalance.replace(/[^0-9.]+/g, ''));

    const handleComplete = useCallback(() => {
        setIsRolling(false);
        onAnimationComplete();
    }, [onAnimationComplete]);

    const animate = useCallback((timestamp: number) => {
        if (startTimeRef.current === undefined) {
            startTimeRef.current = timestamp;
        }
        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(1, elapsed / duration);
        const numericTarget = isNaN(targetNumericValue) ? 0 : targetNumericValue;
        const value = progress * numericTarget;
        // 3. Prepend the extracted symbol to the formatted display value
        setDisplayValue(`${currencySymbol}${value.toFixed(2)}`);

        if (progress < 1) {
            frameRef.current = requestAnimationFrame(animate);
        } else {
            // Ensure final display also includes the symbol
            setDisplayValue(`${currencySymbol}${numericTarget.toFixed(2)}`);
            handleComplete();
        }
    }, [targetNumericValue, duration, handleComplete, currencySymbol]);

    useEffect(() => {
        // Set initial display value including the correct symbol
        setDisplayValue(`${currencySymbol}0.00`);
        setIsRolling(true);
        startTimeRef.current = undefined;
        frameRef.current = requestAnimationFrame(animate);
        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
        };
    }, [animate, currencySymbol]);
    // --- End Rolling Number Logic ---

    // Memoize the final spark settings to stabilize the reference
    const finalSparkSettings = useMemo(() => ({
         ...defaultSparkSettingsConst, // Use the constant defined outside
         ...sparkSettings 
    }), [sparkSettings]); // Only recalculate if the sparkSettings prop changes

    // Style for the hidden span (must match visible text style for accurate measurement)
    const hiddenStyle: React.CSSProperties = {
        position: 'absolute',
        visibility: 'hidden',
        height: 'auto',
        width: 'auto',
        whiteSpace: 'nowrap', // Prevent wrapping
        fontWeight: "bold",
        fontSize: `${fontSize}px`,
        zIndex: -1, // Ensure it's not visible or interactive
    };

    // Style for the visible text container
    const visibleTextStyle: React.CSSProperties = {
        fontWeight: "bold",
        fontSize: `${fontSize}px`,
        color: "var(--secondary-color)",
        marginRight: 5,
        zIndex: 1,
        textAlign: 'right',
        display: 'inline-block',
        // Apply calculated width if available, otherwise use auto
        width: textWidth !== null ? `${textWidth}px` : 'auto',
        // Optional: Add transition for smoother width application if needed, 
        // but might be unnecessary if measurement is fast.
        // transition: 'width 0.1s ease-out',
    };

    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            {/* Hidden span for measurement */}
            <span ref={hiddenSpanRef} style={hiddenStyle}>{targetBalance}</span>

            {/* Visible rolling number */}
            <BlockyHeavyText style={visibleTextStyle}>
                {displayValue}
            </BlockyHeavyText>
            
            {/* Render sparks conditionally or pass isRolling state */}
            <SparksRenderer {...finalSparkSettings} isAnimating={isRolling} /> 
        </div>
    );
};

export default AnimatedBalanceDisplay; 